import { useEffect, useMemo, useState } from "react";
import { AppState } from "@/store/root.reducer";
import { Artwork } from "@/types/artworksData";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import styles from "./modal.module.css";
import { ApiClient } from "../../utils/apiClient";
import { ModelViewer } from "../3d/model.viewer";
import "./buy.artwork.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faEnvelope, faExternalLink, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import ZoomViewer from "../3d/zoom.viewer";
import ArtworkImageCarousel from "../ArtworkImageCarousel";
import { RightsStatementLink } from "../../utils/rightsStatement.utils";
import { resolveArtworkViewerMedia } from "../3d/media.viewer.utils";
import type { ModelLoadSource } from "../3d/utils/general.utils";
import { getProxiedImageUrl, getProxiedSoundUrl } from "../../utils/imageProxy";
import {
    descriptionParagraphs,
    ImportedMetadataDetails,
    PLACEHOLDER_IMPORT_MESSAGE,
    isEuropeanaImportSource,
    isImageLikeUrl,
    isPlaceholderImport,
    normalizePseudoMoMDisplayYear,
    resolveEuropeanaImportSourceDisplay,
    resolveEuropeanaProviderDisplay,
} from "../artworks/importedMetadata";

const BASE_API = process.env.REACT_APP_BASE_API;
type Props = {
  slot: number;
  show: boolean;
  onHide: () => void;
  artwork: Artwork | null;
  galleryId: string;
  onBuyHandler: (artworkData: any) => void;
  onEnquireHandler: () => void;
  successNotify: (message: string) => void;
  failedNotify: (message: string) => void;
  canAddArtworkToSlot?: boolean;
  onAddArtworkToSlot?: () => void;
  canRemoveArtworkFromSlot?: boolean;
  onRemoveArtworkFromSlot?: () => void;
  isPseudoMoMArtwork?: boolean;
  offlineMode?: boolean;
};

export const VIDEO_EXT = [
    'mp4', 'm4a', 'f4v', 'mov'
]

export const MODEL_3D_EXT = [
    'glb', 'gltf', 'obj', 'fbx', '3ds', 'dae', 'ply', 'stl'
]

const isUnsupportedModelUrl = (value?: string): boolean => {
    if (!value) return true;
    const lower = value.toLowerCase();
    return (
        lower.includes('/embed') ||
        lower.includes('/player') ||
        lower.endsWith('.html') ||
        lower.endsWith('.htm') ||
        /\.(jpe?g|png|gif|webp|svg)(?:[?#]|$)/i.test(value)
    );
}

const isUnsupportedExternalImageUrl = (value?: string): boolean => {
    if (!value) return true;
    const lower = value.toLowerCase();
    return (
        lower.includes('/embed') ||
        lower.includes('/player') ||
        lower.includes('/watch') ||
        lower.includes('/alacarta/') ||
        lower.endsWith('.html') ||
        lower.endsWith('.htm')
    );
}

export const BuyArtworkModal = ({show, artwork, onHide, onEnquireHandler, successNotify, failedNotify, canAddArtworkToSlot = false, onAddArtworkToSlot, canRemoveArtworkFromSlot = false, onRemoveArtworkFromSlot, isPseudoMoMArtwork = false, offlineMode = false}: Props) => {
    const apiClient = useMemo(() => ApiClient(BASE_API), []);

    const { user } = useSelector((state: AppState) => state.auth);
    const isPseudoMoMDetailMode = Boolean(canAddArtworkToSlot || canRemoveArtworkFromSlot);
    const usePseudoMoMMetadata = isPseudoMoMArtwork || isPseudoMoMDetailMode;
    const [ mediaType, setMediaType ] = useState<string>();
    const [ originalImage, setOriginalImage ] = useState<string>();
    const [ imageUrls, setImageUrls ] = useState<string[]>([]);
    const [ modelSource, setModelSource ] = useState<ModelLoadSource | null>(null);
    const [ isDownloading, setIsDownloading ] = useState<boolean>(false);
    const isArtetraHosted = !artwork?.source || [artwork.source.sourceName, artwork.source.provider]
        .some((source) => source?.toLowerCase() === 'artetra');
    const publicDownloadEnabled = artwork?.edmProfile?.publicDownloadEnabled !== false;
    const publicDownloadUrl = offlineMode ? "" : (artwork?.artworkData?.dataOriginalUrl || originalImage);
    const hasDownloadUrl = Boolean(publicDownloadUrl);
    const showPublicDownloadAction = !offlineMode && publicDownloadEnabled && hasDownloadUrl;
    const displayImageUrl = originalImage
        ? (offlineMode ? getProxiedImageUrl(originalImage, BASE_API || '') : originalImage)
        : "";
    const providerDisplay = artwork?.source
        ? (usePseudoMoMMetadata && isEuropeanaImportSource(artwork.source)
            ? resolveEuropeanaProviderDisplay(artwork.source)
            : (resolveEuropeanaProviderDisplay(artwork.source) || artwork.source.provider || ""))
        : "";
    const europeanaYearDisplay = artwork?.source && usePseudoMoMMetadata
        ? normalizePseudoMoMDisplayYear((artwork.source as any).year, artwork.edmProfile?.contextEntities)
        : String((artwork?.source as any)?.year || "");

    const onDownloadHandler = async () => {
        if (offlineMode || !publicDownloadUrl) return;

        // Ensure user has scrolled to the bottom of the info section before downloading
        // If not at bottom, scroll to bottom and exit early
        const contentWrapper = document.getElementById('content-wrapper') as HTMLElement | null;
        if (contentWrapper) {
            const atBottom = contentWrapper.scrollTop + contentWrapper.clientHeight >= contentWrapper.scrollHeight - 51;
            if (atBottom) {
                setIsDownloading(true);
            } else {
                if (typeof (contentWrapper as any).scrollTo === 'function') {
                    contentWrapper.scrollTo({ top: contentWrapper.scrollHeight, behavior: 'smooth' });
                } else {
                    contentWrapper.scrollTop = contentWrapper.scrollHeight;
                }
                return;
            }
        }
        
        try {
            console.log('Download handler called for artwork:', artwork.title);
            console.log('Artwork source:', artwork.source);
            console.log('Original image URL:', originalImage);
            
                if (artwork.source) {
                // Download external artwork
                const downloadUrl = publicDownloadUrl;
                const filename = generateFilename(downloadUrl, artwork.title, artwork.artistName);
                console.log('External download - URL:', downloadUrl, 'Filename:', filename);
                const preopened = window.open('', '_blank');
                await downloadFile(downloadUrl, filename, preopened);
            } else {
                // Download local artwork
                if (originalImage) {
                    const filename = generateFilename(originalImage, artwork.title, artwork.artistName);
                    console.log('Local download - URL:', originalImage, 'Filename:', filename);
                    const preopened = window.open('', '_blank');
                    await downloadFile(originalImage, filename, preopened);
                } else {
                    console.log('No original image available, showing enquire handler');
                    onEnquireHandler();
                }
            }
        } catch (error) {
            console.error('Error in download handler:', error);
        } finally {
            setIsDownloading(false);
        }
    }

    const generateFilename = (fileUrl: string, title: string, artistName: string) => {
        // Clean title and artist name for filename
        const cleanTitle = title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
        const cleanArtist = artistName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
        
        // Extract file extension more carefully
        let extension = 'jpg'; // default
        
        try {
            // Remove query parameters and fragments first
            const urlWithoutParams = fileUrl.split('?')[0].split('#')[0];
            
            // Get the last part after the last dot
            const lastDotIndex = urlWithoutParams.lastIndexOf('.');
            if (lastDotIndex !== -1) {
                const potentialExt = urlWithoutParams.substring(lastDotIndex + 1).toLowerCase();
                
                // Check if it's a valid image/video/3d model extension
                const validExtensions = [
                    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif',
                    'mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v',
                    'glb', 'gltf', 'obj', 'fbx', '3ds', 'dae', 'ply', 'stl'
                ];
                
                if (validExtensions.includes(potentialExt)) {
                    extension = potentialExt;
                }
            }
        } catch (error) {
            console.log('Error extracting extension, using default:', error);
        }
        
        const filename = `${cleanTitle}_by_${cleanArtist}.${extension}`;
        console.log('Generated filename:', filename, 'from URL:', fileUrl);
        return filename;
    }

    const getExtensionFromContentType = (contentType: string) => {
        const typeMap: { [key: string]: string } = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/bmp': 'bmp',
            'image/webp': 'webp',
            'image/svg+xml': 'svg',
            'image/tiff': 'tiff',
            'video/mp4': 'mp4',
            'video/avi': 'avi',
            'video/quicktime': 'mov',
            'video/x-msvideo': 'avi',
            'video/webm': 'webm',
            'model/gltf-binary': 'glb',
            'model/gltf+json': 'gltf',
            'application/octet-stream': 'bin'
        };
        
        return typeMap[contentType] || null;
    }

    const downloadFile = async (fileUrl: string, filename: string, preopenedWindow?: Window | null) => {
        try {
            console.log('Starting download:', fileUrl, filename);
            const popupRef: Window | null | undefined = preopenedWindow;
            
            // Try direct download first (for same-origin or CORS-enabled resources)
            const response = await fetch(fileUrl, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache',
            });
            
            console.log('Fetch response:', response.status, response.statusText);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
            }
            
            // Convert to blob
            const blob = await response.blob();
            console.log('Blob created:', blob.size, blob.type);
            
            // Update filename based on content type if needed
            let finalFilename = filename;
            if (blob.type) {
                const contentType = blob.type.toLowerCase();
                const extensionFromType = getExtensionFromContentType(contentType);
                if (extensionFromType && !filename.toLowerCase().endsWith(`.${extensionFromType}`)) {
                    // Replace extension with the one from content type
                    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
                    finalFilename = `${nameWithoutExt}.${extensionFromType}`;
                    console.log('Updated filename based on content type:', finalFilename);
                }
            }
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = finalFilename;
            link.style.display = 'none';
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            
            // Cleanup with a small delay to ensure download starts
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

            // Close any preopened popup if we did not need it
            if (popupRef && !popupRef.closed) {
                try {
                    popupRef.close();
                } catch (e) {
                    console.log('Unable to close pre-opened window:', e);
                }
            }
            
            console.log('Download initiated successfully');
        } catch (error) {
            console.error('Error downloading file:', error);
            console.log('Trying alternative download method');
            
            // Navigate a pre-opened window (created during the user gesture) to the file URL
            try {
                if (preopenedWindow && !preopenedWindow.closed) {
                    preopenedWindow.location.href = fileUrl;
                    console.log('Navigated pre-opened window to file URL');
                    return;
                }
            } catch (altError) {
                console.error('Failed to navigate pre-opened window:', altError);
            }

            // Last resort: attempt to open a new window (may be blocked if not a user gesture)
            try {
                const newWindow = window.open('', '_blank');
                if (newWindow) {
                    newWindow.location.href = fileUrl;
                    console.log('Opened in new window for download');
                } else {
                    throw new Error('Popup blocked');
                }
            } catch (thirdError) {
                console.error('Final window open also failed:', thirdError);
            }
        }
    }

    useEffect(() => {
        let active = true;
        (async function() {
            if (!artwork) return;

            const dataUrl = artwork.artworkData?.dataUrl || '';
            const fileExt = dataUrl.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '';
            const canResolveSharedMedia =
                artwork.type === "image" || artwork.type === "video" || artwork.type === "3dmodel";
            setImageUrls([]);
            setModelSource(null);

            if (artwork.type === "video" || VIDEO_EXT.includes(fileExt)) {
                setOriginalImage(artwork.artworkData.dataUrl);
                setMediaType('video');
                return;
            }
            
            if (artwork.type === "sound") {
                setOriginalImage(artwork.artworkData.dataUrl);
                setMediaType('sound');
                return;
            }

            if (isPlaceholderImport(artwork.source) && artwork.source?.externalMediaStatus?.placeholderMediaType === "image") {
                const placeholderImageUrl = offlineMode
                    ? (artwork.artworkData.dataUrl || "")
                    : (artwork.artworkData.dataUrl || artwork.artworkData.dataOriginalUrl || artwork.source?.thumbnailUrl || "");
                setOriginalImage(placeholderImageUrl);
                setMediaType(placeholderImageUrl ? 'image' : 'unavailable');
                return;
            }

            if (artwork.type === "3dmodel" || MODEL_3D_EXT.includes(fileExt)) {
                const modelUrl = offlineMode
                    ? artwork.artworkData.dataUrl
                    : (artwork.artworkData.dataUrl || artwork.artworkData.dataOriginalUrl);
                setOriginalImage(modelUrl);
                setMediaType(isImageLikeUrl(modelUrl)
                    ? 'image'
                    : isUnsupportedModelUrl(modelUrl) ? 'unavailable' : '3dmodel');
                if (offlineMode) {
                    return;
                }
            }

            if (!offlineMode && canResolveSharedMedia) {
                try {
                    const resolvedMedia = await resolveArtworkViewerMedia(artwork, "gallery", {
                        apiClient,
                        baseAPI: BASE_API,
                    });
                    if (!active || !resolvedMedia) {
                        if (artwork.type === "image" && artwork.source) {
                            setOriginalImage(artwork.artworkData.dataOriginalUrl || artwork.artworkData.dataUrl || "");
                            setMediaType('unavailable');
                            return;
                        }
                        if (artwork.type === "3dmodel") {
                            setMediaType('unavailable');
                        }
                        return;
                    }

                    setMediaType(resolvedMedia.kind);
                    if (resolvedMedia.kind === "image") {
                        setOriginalImage(resolvedMedia.imageUrl || "");
                        setImageUrls(resolvedMedia.imageUrls || []);
                        setModelSource(null);
                        return;
                    }

                    if (resolvedMedia.kind === "video") {
                        setOriginalImage(resolvedMedia.videoUrl || "");
                        setImageUrls([]);
                        setModelSource(null);
                        return;
                    }

                    setOriginalImage(typeof resolvedMedia.modelSource === "string" ? resolvedMedia.modelSource : "");
                    setImageUrls([]);
                    setModelSource(resolvedMedia.modelSource || null);
                } catch (error) {
                    if (!active) return;
                    console.error("Failed to resolve artwork media", error);
                    failedNotify("Unable to load artwork media.");
                    setMediaType('unavailable');
                }
                return;
            }

            if (offlineMode) {
                const displayUrl = artwork.artworkData?.dataUrl || artwork.artworkData?.thumbnailUrl || "";
                setOriginalImage(displayUrl);
                setMediaType(displayUrl ? 'image' : 'unavailable');
                return;
            }

            // check artwork source 
            if (artwork.source) {
                const sourceUrl = offlineMode
                    ? (artwork.artworkData.dataUrl || artwork.artworkData.thumbnailUrl)
                    : (artwork.artworkData.dataOriginalUrl || artwork.artworkData.dataUrl);
                setOriginalImage(sourceUrl);
                setMediaType(isUnsupportedExternalImageUrl(sourceUrl) ? 'unavailable' : 'image');
            } else if (offlineMode) {
                const displayUrl = artwork.artworkData?.dataUrl || artwork.artworkData?.thumbnailUrl;
                setOriginalImage(displayUrl);
                setMediaType(displayUrl ? 'image' : 'unavailable');
            } else if (artwork.artworkData?.dataOriginalUrl || artwork.artworkData?.dataUrl) {
                setOriginalImage(artwork.artworkData.dataOriginalUrl || artwork.artworkData.dataUrl);
                setMediaType('image');
            } else {
                try {
                    const d = await apiClient.getOriginalImage(artwork._id);
                    if (!active) return;
                    setOriginalImage(d);
                    setMediaType('image');
                } catch (error) {
                    if (!active) return;
                    console.error("Failed to load original artwork image", error);
                    failedNotify("Unable to load artwork media.");
                    setMediaType('unavailable');
                }
            }
        })()

        return () => {
            active = false;
        };
    }, [apiClient, artwork, failedNotify, offlineMode])

    const toTitleCase = (text: string) => {
        if (!text) return text;
        return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    };

    const handleDownloadAttachment = async (artworkId: string, attachmentId: string, attachmentName: string) => {
        if (offlineMode) return;

        try {
            const response = await apiClient.downloadArtworkAttachment(artworkId, attachmentId);
            if (!response) {
                failedNotify('Failed to download attachment');
                return;
            }

            const blob = new Blob([response.data]);

            const contentDisposition = response.headers['content-disposition'];
            let filename = attachmentName;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename\*?=UTF-8''(.*)$/);
                if (match) {
                    filename = decodeURIComponent(match[1]);
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading attachment:', error);
            failedNotify('Failed to download attachment');
        }
    }

    if (!artwork) return null;

    return (
     <>
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            dialogClassName="buy-artwork-modal"
            contentClassName="modal-no-padding"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            backdrop="static"
        >
            <Modal.Body className={styles.background} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <div className="bg-dark-800 d-flex" style={{ flex: 1, minHeight: 0 }}>
					<div
						id="image-section"
						className="bg-dark-900"
						style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}
						onContextMenu={(e) => e.preventDefault()}
						onDragStart={(e) => e.preventDefault()}
					>
                        {(mediaType === "image" && originalImage) ? (
							<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
								<ArtworkImageCarousel
									images={imageUrls.length > 0
                                        ? imageUrls
                                        : ((!offlineMode && artwork?.source?.images && artwork.source.images.length > 0)
                                            ? artwork.source.images
                                            : [displayImageUrl])}
									alt={artwork?.title ?? ""}
									renderSlide={(src) => <ZoomViewer src={src} className="" />}
									carouselClassName={styles["artwork-carousel"]}
									fillHeight
								/>
							</div>
						) : null}
                        {(mediaType === "video" && originalImage) ? 
							<video controls controlsList={offlineMode ? "nodownload" : undefined} src={originalImage} style={{width: "100%"}} muted draggable={false}></video> : <></>
                        }
                        {(mediaType === "sound" && originalImage) ?
                            <div className="d-flex align-items-center justify-content-center" style={{width: "100%", height: "100%"}}>
                                <audio controls controlsList={offlineMode ? "nodownload" : undefined} src={getProxiedSoundUrl(originalImage, BASE_API || '')} />
                            </div> : <></>
                        }
                        {(mediaType === "3dmodel" && (modelSource || originalImage)) ? 
							<div className="d-flex align-items-center justify-content-center" style={{width: "100%", height: "100%"}}>
								<div 
									ref={(el) => {
										if (el) {
											const viewer = new ModelViewer();
											viewer.init(el, modelSource || originalImage);
										}
									}}
									style={{width: "100%", height: "400px"}}
									draggable={false}
								/>
							</div> : <></>
						}
                        {mediaType === "unavailable" ? (
                            <div className="d-flex align-items-center justify-content-center text-white-50 p-5" style={{width: "100%", height: "100%"}}>
                                Media unavailable. Reimport this Europeana artwork after media resolution is available.
                            </div>
                        ) : null}
                        <div className="position-absolute top-0 end-0 m-4" style={{ zIndex: 20 }}>
                            <button id="close-btn" className="btn-close-custom" onClick={onHide} aria-label="Close">
                                <svg
                                    className="text-white fs-5"
                                    width="1em"
                                    height="1em"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                    focusable="false"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div id="info-section" className="bg-dark-800">
                        <div id="content-wrapper" className="p-4">
                            <div id="object-details">
                                {artwork.artistName && artwork.artistName !== "" && <div id="artist-info" className="mb-4">
                                    <p className="text-accent-green text-uppercase-small mb-2">{usePseudoMoMMetadata ? "Creator" : "Artist"}</p>
                                    <h2 className="text-white fs-3 fw-bold mb-0">{artwork.artistName}</h2>
                                </div>}
                                {artwork.title && artwork.title !== "" && <div id="title-info" className="mb-4">
                                    <p className="text-accent-green text-uppercase-small mb-2">Title</p>
                                    <h3 className="text-white fs-4 fw-semibold mb-0">{artwork.title}</h3>
                                </div>}
                                {(artwork.source && artwork.source.sourceName?.toLowerCase() === 'europeana'
                                    ? !!europeanaYearDisplay
                                    : !!artwork.createdAt) && (
                                    <div id="year-info" className="mb-4">
                                        <p className="text-accent-green text-uppercase-small mb-2">Year</p>
                                        <p className="text-white-50 mb-0">
                                            {artwork.source && artwork.source.sourceName?.toLowerCase() === 'europeana'
                                                ? europeanaYearDisplay
                                                : new Date(artwork.createdAt).getFullYear()}
                                        </p>
                                    </div>
                                )}

                                {artwork.description && artwork.description !== "" && (artwork.description[0] !== "") && <div id="description-info" className="pt-4 border-top border-dark-600">
                                    <p className="text-accent-green text-uppercase-small mb-2">Description</p>
                                    {descriptionParagraphs(artwork.description).map((desc, i) => (
                                        <p key={i} className={usePseudoMoMMetadata ? "text-white-50 mb-3" : "text-secondary small mb-3"}>{desc}</p>
                                    ))}
                                </div>}                              
                                {artwork.source && providerDisplay && <div id="provider-info" className="border-dark-600">
                                    <p className="text-accent-green text-uppercase-small mb-2">Provider</p>
                                    <p className="text-white-50 mb-0">
                                        {toTitleCase(providerDisplay)}
                                    </p>
                                </div>}
                                {artwork.source && usePseudoMoMMetadata && isEuropeanaImportSource(artwork.source) && (
                                    <div id="import-source-info" className="pt-4 border-dark-600">
                                        <p className="text-accent-green text-uppercase-small mb-2">Import source</p>
                                        <p className="text-white-50 mb-0">{resolveEuropeanaImportSourceDisplay(artwork.source)}</p>
                                    </div>
                                )}
                                {artwork.source &&
                                !isPseudoMoMDetailMode &&
                                (artwork.source.sourceName?.toLowerCase() === 'europeana' || artwork.source.provider?.toLowerCase() === 'europeana') &&
                                artwork.source.edmConceptPrefLabelLangAware &&
                                artwork.source.edmConceptPrefLabelLangAware.length > 0 && (
                                    <div id="labels-info" className="pt-4 border-dark-600">
                                        <p className="text-accent-green text-uppercase-small mb-2">Labels</p>
                                        <p className="text-white-50 mb-0">{artwork.source.edmConceptPrefLabelLangAware.join(', ')}</p>
                                    </div>
                                )}
                                {isPlaceholderImport(artwork.source) && !usePseudoMoMMetadata && (
                                    <div id="media-status-info" className="pt-4 border-dark-600">
                                        <p className="text-accent-green text-uppercase-small mb-2">Media status</p>
                                        <p className="text-white-50 mb-0">{PLACEHOLDER_IMPORT_MESSAGE}</p>
                                    </div>
                                )}
                                <ImportedMetadataDetails
                                    source={artwork.source}
                                    artworkTitle={artwork.title}
                                    artworkDescription={artwork.description}
                                    artworkYear={usePseudoMoMMetadata ? europeanaYearDisplay : ((artwork.source as any)?.year || "")}
                                    pseudoMoMReadMode={usePseudoMoMMetadata}
                                    hideHeading={usePseudoMoMMetadata}
                                    className="pt-4 border-dark-600"
                                    labelClassName="text-accent-green text-uppercase-small mb-2"
                                    valueClassName="text-white-50 mb-0"
                                    contextEntities={artwork.edmProfile?.contextEntities}
                                    artworkTags={artwork.tags}
                                />

                            
                            {artwork.physical && artwork.physicalMeta && <>
                                {artwork.physicalMeta.format && artwork.physicalMeta.format !== "" && <div id="format-info" className="pt-4 border-dark-600">
                                    <p className="text-accent-green text-uppercase-small mb-2">Format</p>
                                    <p className="text-white-50 mb-0">{artwork.physicalMeta.format}</p>
                                </div>}
                                {artwork.physicalMeta.materials && artwork.physicalMeta.materials !== "" && <div id="materials-info" className="pt-4 border-dark-600">
                                    <p className="text-accent-green text-uppercase-small mb-2">Materials</p>
                                    <p className="text-white-50 mb-0">{artwork.physicalMeta.materials}</p>
                                </div>}
                                {artwork.physicalMeta.dateOfCreation && <div id="date-of-creation-info" className="pt-4 border-dark-600">
                                    <p className="text-accent-green text-uppercase-small mb-2">Date of Creation</p>
                                    <p className="text-white-50 mb-0">{new Date(artwork.physicalMeta.dateOfCreation).toLocaleDateString()}</p>
                                </div>}
                            </>}
                            {((artwork.source?.rights?.length && artwork.source.rights[0] !== "") || (artwork.physical && artwork.attachments?.length)) ? (
                            <div id="display-rights" className="info-card p-4 mt-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <FontAwesomeIcon icon={faShieldHalved} className="text-accent-green" />
                                    <h3 className="text-accent-green text-uppercase-small mb-0">Display Rights & Copyright</h3>
                                </div>
                                <div className="space-y-3 text-sm">
                                    {artwork.source?.rights?.length > 0 && artwork.source.rights[0] !== "" && (
                                        <div className="mb-4">
                                            <p className="text-white font-medium">
                                                <FontAwesomeIcon icon={faCircleCheck} className="text-accent-green text-xs mt-1" />
                                                &nbsp;Rights Statement
                                            </p>
                                            <RightsStatementLink rightsUrl={artwork.source.rights[0]} />
                                        </div>
                                    )}
                                    {artwork.source?.sourceName === "europeana" && (
                                        <div id="listing-source-info" className="border-dark-600">
                                            <p className="text-white font-medium">
                                                <FontAwesomeIcon icon={faCircleCheck} className="text-accent-green text-xs mt-1" />
                                                &nbsp;Listing Source
                                            </p>
                                            <p className="text-white-50 mb-0 text-break">{artwork.source.itemUrl}</p>
                                        </div>
                                    )}
                                    {artwork.physical && artwork.attachments && artwork.attachments.length > 0 && (
                                        <div id="authenticity-docs" className="border-dark-600">
                                            <p className="text-white font-medium mb-2">
                                                <FontAwesomeIcon icon={faCircleCheck} className="text-accent-green text-xs mt-1" />
                                                &nbsp;Authenticity documents
                                            </p>
                                            <div>
                                                {artwork.attachments.map((attachment, i) => (
                                                    <div key={i}>
                                                        <p className="text-white font-medium mb-1 text-break">
                                                            {offlineMode ? (
                                                                <span className="text-white-50">{attachment.name}</span>
                                                            ) : (
                                                                <a
                                                                    className="text-white-50 text-decoration-none"
                                                                    onClick={() => handleDownloadAttachment(artwork._id, attachment._id, attachment.name)}
                                                                    style={{cursor: "pointer"}}
                                                                >
                                                                    {attachment.name}&nbsp;<FontAwesomeIcon
                                                                        icon={faExternalLink}
                                                                        className="ms-2 text-accent-green"
                                                                    />
                                                                </a>
                                                            )}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            ) : null}
                            </div>
                        </div>
                        <div id="action-buttons" className="p-4 bg-dark-900 border-top border-dark-600">
                            {offlineMode ? (
                                <p className="text-white-50 small mb-3">Offline copy. Network actions are unavailable.</p>
                            ) : null}
                            {canAddArtworkToSlot ? (
                                <button
                                    id="add-to-pillar-btn"
                                    className="btn btn-primary-custom w-100 mb-3 py-3 d-flex align-items-center justify-content-center gap-3 fw-semibold"
                                    onClick={onAddArtworkToSlot}
                                >
                                    <span>Add artwork to this pillar</span>
                                </button>
                            ) : null}
                            {canRemoveArtworkFromSlot ? (
                                <button
                                    id="remove-from-pillar-btn"
                                    className="btn btn-secondary-custom w-100 mb-3 py-3 d-flex align-items-center justify-content-center gap-3 fw-semibold"
                                    onClick={onRemoveArtworkFromSlot}
                                >
                                    <span>Remove artwork from this pillar</span>
                                </button>
                            ) : null}
							{!usePseudoMoMMetadata && <div
                                data-tooltip={offlineMode
                                    ? "Enquiries are unavailable offline"
                                    : !( !artwork.source || (artwork.source.sourceName?.toLowerCase() === 'artetra' || artwork.source.provider?.toLowerCase() === 'artetra') )
                                    ? "This artwork is hosted on a partner site. You can only use the Enquiry functionality for artworks hosted on Artetra"
                                    : undefined}
                            >
                                <button 
                                    id="enquire-btn" 
                                    className="btn btn-primary-custom w-100 mb-3 py-3 d-flex align-items-center justify-content-center gap-3 fw-semibold" 
                                    disabled={offlineMode || !isArtetraHosted}
                                    onClick={onEnquireHandler}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} className="fs-5" />
                                    <span>Enquire About This Work</span>
                                </button>
                            </div>}
                            
                            {showPublicDownloadAction ? (
                                <div
                                    data-tooltip={(!isDownloading && !isArtetraHosted)
                                        ? "Review the Copyright section and download this artwork"
                                        : undefined}
                                >
                                    <button 
                                        id="download-btn" 
                                        className="btn btn-secondary-custom w-100 py-3 d-flex align-items-center justify-content-center gap-3 fw-semibold"
                                        onClick={onDownloadHandler}
                                        disabled={isDownloading || !publicDownloadUrl || isArtetraHosted}
                                    >
								    <FontAwesomeIcon icon={faDownload} className="fs-5" />
                                    <span>Download Artwork</span>
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>            
      </Modal.Body>
    </Modal>
    </>
  );
};
